import React from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import NextLink from 'next/link';
import {
  Box,
  Row,
  Col,
  Title,
  Container,
  Text,
  BodyTitle,
  Link,
  Breadcrumbs,
  Flex,
} from '@firsttable/web-components/atoms';
import { SpaceProps } from 'styled-system';
import { PageBreadCrumb } from '@firsttable/types';
import IconArrowRightM from '@firsttable/web-components/atoms/Icon/IconArrowRightM';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import GatsbyLinkConvertor from '../../GatsbyLinkConvertor';

const BackgroundWrap = styled(Box)`
  position: absolute;
  height: 100%;
  width: 100vw;
`;

const Gradient = styled(Box)`
  position: absolute;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.6));
  mix-blend-mode: normal;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  opacity: 1 !important;
  z-index: 10;
  height: 100%;
  width: 100%;
`;
const RelativeWrap = styled(Box)`
  position: relative;
  z-index: 0;
`;
const ContentWrap = styled(Box)`
  position: relative;
  z-index: 11;
  height: 100%;
`;

const StyledImageBackground = ({ children, ...props }: { children: any } & SpaceProps) => (
  <RelativeWrap height={[275, 643, 464]}>
    <Gradient {...props} />
    <BackgroundWrap>
      <Image
        alt="First Table"
        src="/images/home-bgv5.jpg"
        sizes="100vw"
        fill
        style={{
          objectFit: 'cover',
          objectPosition: 'center top',
        }}
        quality={50}
        priority
      />
    </BackgroundWrap>
    <ContentWrap>{children}</ContentWrap>
  </RelativeWrap>
);
StyledImageBackground.propTypes = {
  children: PropTypes.node,
};

const HeroCTA = styled(Box)`
  width: 400px;
  background: ${themeGet('colors.white')};
  transform: matrix(1, 0.03, -0.02, 1, 0, 0);
  .expandable-pill {
    white-space: nowrap;
    transition: 0.3s ease;
  }
  &:hover .expandable-pill {
    width: 185px;
  }
`;

const HeroCTABody = styled(Box)`
  transform: matrix(1, -0.03, 0.02, 1, 0, 0);
  padding: 35px 25px;
  text-align: center;
`;

const HeroCTABackground = styled(Box)`
  height: 100%;
  background: ${themeGet('colors.grayscale.300')};
  transform: matrix(1, -0.03, -0.02, -1, 0, 0);
  position: absolute;
  right: 0;
  top: 0;
  left: 0;
`;

const FaqLink = styled(Box)`
  position: absolute;
  bottom: -32px;
  left: 50%;
  transform: translate(-50%, 0);
  .arrow {
    flex-shrink: 0;
    transition: 0.2s ease-out transform;
    will-change: transform;
  }
`;

const CityHero = ({
  headerTitle,
  headerSubtitle,
  infoTitle,
  infoDescription,
  breadcrumbs,
  showFaqLink = true,
  subtextCta = null,
}: {
  headerTitle: React.ReactNode;
  headerSubtitle: React.ReactNode;
  infoTitle: React.ReactNode;
  infoDescription: React.ReactNode;
  breadcrumbs: PageBreadCrumb[];
  showFaqLink?: boolean;
  subtextCta?: React.ReactNode;
}) => (
  <StyledImageBackground pb={[0, 55]}>
    <Container
      display="flex"
      flexDirection="column"
      height="100%"
      justifyContent={['start', 'start', 'center']}>
      <Row>
        <Col>
          <TrackProvider component_name="breadcrumbs">
            <Breadcrumbs
              pb={0}
              pt={23}
              color="white"
              showShadowOverlay={false}
              itemScope
              itemType="http://schema.org/BreadcrumbList">
              {breadcrumbs.map(({ node }, i) => (
                <li
                  key={node.slug}
                  itemScope
                  itemProp="itemListElement"
                  itemType="http://schema.org/ListItem">
                  <TrackProvider link_name="current breadcrumb">
                    <Link NavComponent={GatsbyLinkConvertor} to={node.slug} itemProp="item">
                      <Box as="span" itemProp="name">
                        {node.menuTitle}
                      </Box>
                    </Link>
                  </TrackProvider>
                  <meta itemProp="position" content={`${i + 1}`} />
                </li>
              ))}
            </Breadcrumbs>
          </TrackProvider>
        </Col>
      </Row>
      <Row alignItems="center" pb={['62px', '85px']} flex={[1, 1, 0]} pt={20}>
        <Col width={[1, null, 'auto']} textAlign={['center', null, 'left']}>
          <Title
            as="h1"
            size="hero"
            color="white"
            mb={[10, 10]}
            fontSize={[28, 56]}
            lineHeight={1.1}>
            {headerTitle}
          </Title>
          <BodyTitle color="gold.400" m={0} fontWeight={[400, 600]} fontSize={[16, 22]}>
            {headerSubtitle}
          </BodyTitle>
          {subtextCta && (
            <Box display={['flex', 'none']} justifyContent="center" mt={15}>
              {subtextCta}
            </Box>
          )}
        </Col>
        <Col width={[1, null, 'contains']} display={['none', 'block']}>
          <Box position="relative" width="407px" ml="auto" mr={[null, 'auto', 0]} mt={-20}>
            <HeroCTABackground />
            <HeroCTA>
              <HeroCTABody>
                <Title as="h3" size="h4">
                  {infoTitle}
                </Title>
                <Text fontSize="l">{infoDescription}</Text>
                {showFaqLink && (
                  <NextLink href="/frequently-asked-questions" passHref legacyBehavior>
                    <FaqLink as="a">
                      <Flex
                        bg="brand.red"
                        borderRadius="36px"
                        height="64px"
                        width="64px"
                        alignItems="center"
                        justifyContent="flex-end"
                        overflow="hidden"
                        color="white"
                        textAlign="center"
                        className="expandable-pill">
                        <Flex width="185px" className="arrow">
                          <Box width="70%" pl="22px" fontWeight={600}>
                            Learn more
                          </Box>
                          <Flex width="30%" pr="9px" alignItems="center" justifyContent="center">
                            <IconArrowRightM color="inherit" />
                          </Flex>
                        </Flex>
                      </Flex>
                    </FaqLink>
                  </NextLink>
                )}
              </HeroCTABody>
            </HeroCTA>
          </Box>
        </Col>
      </Row>
    </Container>
  </StyledImageBackground>
);

export default React.memo(CityHero);
